/* ==========================================================================
   In-page paging for the news listing.

   Pressing "next" used to be an ordinary navigation: the whole document went
   away, the header and footer were re-parsed, and the reader was dropped at
   the top of a new page with no sense that anything had moved. This fetches
   the next page instead and swaps the results in place, so the band, the tag
   row and the chrome stay put and only the stories change — with the same
   reveal the grid already uses, travelling the way the reader paged.

   IT IS PROGRESSIVE ENHANCEMENT, not a dependency. Every pager link is a real
   href to a real page that renders server-side, so with this file absent, or
   a fetch refused, the browser simply follows the link as it always did — and
   that is also the fallback taken on any error below.

   Contract:
     [data-news-root]     the block, which holds the scroll target
     [data-news-results]  the region replaced, wholesale
     [data-news-page]     a pager link, carrying data-news-dir="1" or "-1"
   ========================================================================== */
(function () {
  'use strict';
  if (window.__epgNewsPager) return;
  window.__epgNewsPager = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function results(root) { return root.querySelector('[data-news-results]'); }

  function swap(root, html, dir, url) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    /* Matched by the block's own scoped class, not by position: a page of
       this theme carries several `[data-news-results]`-shaped regions only if
       someone puts two listings on it, and then each must find its own. */
    var incoming = parsed.querySelector('[data-news-results][data-news-id="' + root.getAttribute('data-news-id') + '"]');
    if (!incoming) return false;

    var current = results(root);
    if (!current) return false;

    /* The height is pinned across the swap so the document does not jump
       while the new page is still arriving — a shorter last page would
       otherwise yank the scroll position up from under the reader. */
    var h = current.getBoundingClientRect().height;
    current.style.minHeight = h + 'px';

    current.parentNode.replaceChild(incoming, current);
    incoming.style.minHeight = h + 'px';

    /* The cards enter from the side they were paged towards, which is the
       rule blocks/product-grid-filters.liquid's grid already runs on. */
    incoming.style.setProperty('--epa-in-dx', reduced ? '0px' : (dir > 0 ? '26px' : '-26px'));

    var scope = root.querySelector('[data-epg-reveal-scope]') || root;
    scope.removeAttribute('data-epg-reveal-bound');
    if (window.__epgRevealScan) window.__epgRevealScan();

    /* Let the new page settle before releasing the floor, or the release is
       measured against a grid that has not laid out yet. A forced reflow
       rather than a requestAnimationFrame pair: a backgrounded tab is served
       no frames at all, and this theme has been caught by that before. */
    void incoming.offsetHeight;
    window.setTimeout(function () { incoming.style.minHeight = ''; }, 420);

    if (url && window.history && window.history.pushState) {
      window.history.pushState({ epgNews: true }, '', url);
    }
    return true;
  }

  function goTo(root, url, dir, fallback) {
    root.setAttribute('data-news-busy', '');
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then(function (html) {
        if (!swap(root, html, dir, url)) throw new Error('no results region');
        root.removeAttribute('data-news-busy');
        /* The band is the landmark, not the top of the document: the reader
           asked for more stories, not for the header. `start` rather than a
           pixel offset so a sticky header on any side is accounted for by the
           browser. */
        var top = root.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: Math.max(0, top - 12), behavior: reduced ? 'auto' : 'smooth' });
      })
      .catch(function () {
        /* Anything at all goes wrong and the link does what it would have
           done untouched. Never leave the reader on a page that swallowed
           their click. */
        root.removeAttribute('data-news-busy');
        window.location.href = fallback;
      });
  }

  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('[data-news-page]') : null;
    if (!link) return;
    /* Leave every deliberate escape alone — a new tab, a download, a
       modified click. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    var root = link.closest('[data-news-root]');
    if (!root || !window.fetch || !window.DOMParser) return;
    e.preventDefault();
    goTo(root, link.href, parseInt(link.getAttribute('data-news-dir') || '1', 10), link.href);
  });

  /* Back and forward have to work, or in-page paging has quietly broken the
     one control every reader trusts. */
  window.addEventListener('popstate', function () {
    var root = document.querySelector('[data-news-root]');
    if (!root) return;
    fetch(window.location.href, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) { swap(root, html, -1, null); })
      .catch(function () { window.location.reload(); });
  });
}());
